import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontText, PrimaryButton, SecondaryButton } from './common';
import { useTheme } from '../contexts/ThemeContext';
import { GlobalMenu } from './common/GlobalMenu';
import { localAnalytics } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  isPremium: boolean;
  isAuthenticated: boolean;
  isCampaign: boolean;
  isAnonymous: boolean;
  userCoins?: number;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onCoinsClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  isPremium,
  isAuthenticated,
  isCampaign,
  isAnonymous,
  userCoins = 0,
  menuOpen,
  onMenuToggle,
  onCoinsClick,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { userId } = useAuth();

  const showFullHeader = !isCampaign || (isAuthenticated && !isAnonymous);

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '5px 10px',
      borderBottom: `1px solid ${theme.colors.overlay}`,
      position: 'sticky' as const,
      top: 0,
      backgroundColor: theme.colors.background,
      zIndex: 100,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row' as const,
      cursor: 'pointer',
    },
    appIcon: {
      width: 36,
      height: 36,
      borderRadius: 12,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    appName: {
      color: theme.colors.text,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 15,
      position: 'relative' as const,
    },
    coinsContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    coinIcon: {
      width: 16,
      height: 16,
    },
  };

  const handleLogin = () => {
    localAnalytics().logEvent('ChapterHeaderLoginClicked', {
      screen: 'ChapterHeader',
      action: 'LoginClicked',
    });
  };

  const handleLogoClick = () => {
    localAnalytics().logEvent('HeaderLogoClicked', {
      screen: 'Header',
      action: 'LogoClicked',
      userId,
      isAuthenticated,
      isPremium,
    });
  };

  return (
    <>
      <header style={styles.header}>
        <Link
          to="/"
          style={{ ...styles.headerLeft, textDecoration: 'none' }}
          onClick={handleLogoClick}
        >
          <img
            src="/img/favicon.png"
            alt={t('app_name')}
            style={styles.appIcon}
          />
          <FontText h4 style={styles.appName}>
            {t('app_name')}
          </FontText>
        </Link>
        {showFullHeader && (
          <div style={styles.headerRight}>
            {isAuthenticated && !isAnonymous && !isPremium ? (
              <div
                style={{
                  ...styles.coinsContainer,
                  ...(onCoinsClick ? { cursor: 'pointer' } : {}),
                }}
                onClick={onCoinsClick}
              >
                <FontText small style={{ color: theme.colors.text }}>
                  {userCoins}
                </FontText>
                <img
                  src="/img/books/coins_1000.png"
                  alt={t('coins')}
                  style={styles.coinIcon}
                />
              </div>
            ) : !isAuthenticated || isAnonymous ? (
              <Link to="/auth" style={{ textDecoration: 'none' }}>
                <PrimaryButton onClick={handleLogin} size="small">
                  <FontText>{t('login')}</FontText>
                </PrimaryButton>
              </Link>
            ) : null}
            <SecondaryButton onClick={onMenuToggle} size="small">
              ☰
            </SecondaryButton>
          </div>
        )}
      </header>

      <GlobalMenu isOpen={menuOpen} onClose={() => onMenuToggle()} />
    </>
  );
};
